import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Check } from "lucide-react";
import { PRODUCTS } from "../data/products";
import { QuickCheckout } from "./QuickCheckout";
import { useCart } from "../context/CartContext";

const ROTATE_MS = 6000;

export const FeaturedProduct = () => {
  const [index, setIndex] = useState(0);
  const product = PRODUCTS[index];
  const { add } = useCart();
  const [added, setAdded] = useState(false);
  const [showPay, setShowPay] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIndex((i) => (i + 1) % PRODUCTS.length), ROTATE_MS);
    return () => clearTimeout(t);
  }, [index]);

  const addToBag = () => {
    add(product.id);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <section
      className="relative min-h-screen flex items-center max-w-[1600px] mx-auto px-6 md:px-12 py-28"
      data-testid="spotlight-section"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 items-center w-full">
        <div className="md:col-span-7">
          <div className="relative overflow-hidden border border-white/5 h-[55vh] md:h-[72vh]">
            <AnimatePresence mode="wait">
              <motion.img
                key={product.id}
                src={product.image}
                alt={product.alt}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.985 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="spotlight-image"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent pointer-events-none" />
          </div>
        </div>

        <div className="md:col-span-4 md:col-start-9">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">The Spotlight</p>
          <AnimatePresence mode="wait">
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-ash mb-3">{product.edition}</p>
              <h2 className="font-serif text-6xl md:text-7xl font-light text-white leading-none" data-testid="spotlight-name">
                {product.name}
              </h2>
              <p className="mt-4 font-serif italic text-xl text-champagne">{product.tagline}</p>
              <div className="mt-6 flex items-baseline gap-4">
                <p className="font-serif text-3xl text-champagne" data-testid="spotlight-price">
                  ${product.price}
                </p>
                <p className="text-base text-ash/70 line-through" data-testid="spotlight-compare-at">
                  ${product.compareAt}
                </p>
                <span className="text-[10px] uppercase tracking-[0.25em] text-gold border border-gold/40 rounded-full px-3 py-1">
                  Launch Offer
                </span>
              </div>
              <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-ash/60" data-testid="spotlight-launch-ends">
                Launch pricing ends soon
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={() => setShowPay(true)}
              className="group relative inline-flex items-center gap-2 rounded-full border border-gold/60 px-8 py-3.5 text-xs uppercase tracking-[0.25em] text-gold overflow-hidden transition-colors duration-500 hover:text-ink"
              data-testid="spotlight-buy-button"
            >
              <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
              <span className="relative">Acquire</span>
              <ArrowUpRight className="relative h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={addToBag}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ash hover:text-gold transition-colors duration-500"
              data-testid="spotlight-add-bag"
            >
              {added ? (
                <>
                  <Check className="h-4 w-4 text-gold" /> Added
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" /> Add to Bag
                </>
              )}
            </button>
          </div>

          <Link
            to={`/product/${product.id}`}
            className="mt-6 inline-block text-xs uppercase tracking-[0.25em] text-ash hover:text-gold transition-colors duration-500"
            data-testid="spotlight-view-link"
          >
            View the frame →
          </Link>

          <div className="mt-12 flex items-center gap-6" data-testid="spotlight-indexer">
            {PRODUCTS.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setIndex(i)}
                className="group relative pb-2"
                data-testid={`spotlight-index-${i + 1}`}
                aria-label={`Show ${p.name}`}
              >
                <span
                  className={`text-xs tracking-[0.2em] transition-colors duration-500 ${
                    i === index ? "text-gold" : "text-ash/50 group-hover:text-ash"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                {i === index && (
                  <motion.span
                    key={index}
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
                    className="absolute bottom-0 left-0 h-px bg-gold"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
      <QuickCheckout
        items={showPay ? [{ lookup_key: product.lookupKey, quantity: 1 }] : null}
        title={`${product.name} — $${product.price}`}
        onClose={() => setShowPay(false)}
      />
    </section>
  );
};
